/**
 * Client-side Annotation related definitions and helpers
 * @module
 */
import * as _ from 'lodash'
export { Range, TypedRange, Label, Section, Sentence, Annotation } from 'services/extract/types'

// export interface SearchTerm {
//   term: Term
//   type: string
// }

export const ENTITIES = [
	'outcome',
	'characteristic',
	'nct-id',
	'p-value',
	'a-value',
	'group-n',
	'ci',
	'hr',
	'continuous-value',
	'timepoint',
	'dosage',
	'intervention',
	'binary-value',
	'measure',
	'quantity',
	'pos-num',
	'misc',
	'unknown'
]

export const ENTITY_PRECEDENCE = _.transform(ENTITIES.slice().reverse(), (dict, entity, i) => {
	dict[entity] = i
})

/**
 * Governs which type of annotations are assignable (which clicked, selected) to what target field types
 * @see ExtractField.ftype
 */
export const ASSIGNABLE = {
	'group-n': ['group-n', 'pos-int'],
	'dosage': ['dosage'],
	'timepoint': ['timepoint', 'pos-int'],
	'binary-value': ['binary-value', 'pos-num', 'pos-pct'],
	'count-value': ['count-value', 'pos-num'],
	'continuous-value': ['continuous-value', 'quantity', 'pos-num'],
	'ci': ['ci'],
	'sd': ['sd', 'pos-num'],
	'se': ['sd', 'pos-num'],
	'p-value': ['p-value', 'pos-num'],
}

export const findSection = r => r ? (r.kind === 'section' ? r : (r.section ? r.section : /*XXX this is wrong, don't follow parent*/ findSection(r.parent))) : undefined
export const findSentence = r => r ? (r.kind === 'sentence' ? r : (r.sentence ? r.sentence : /*XXX this is wrong, don't follow parent*/ findSentence(r.parent))) : undefined
export const measureValue = m =>
	m.parsed?.quantifier ? m.parsed.quantifier.Gestalt : (m.captured || m.text)

export abstract class Intervals<T extends Range> {
	abstract getIntervals(): T[]
	abstract size(): number
	abstract insert(...ranges: T[]): boolean
	abstract append(intervals: Intervals<T>): this
	abstract filter(fn: (t: T) => boolean): this
	abstract detectOverlap(range: Range, sameSource?: boolean): T
	abstract detectExact(range: Range): T
	abstract removeOverlapping()
}

const SOURCE_PRECEDENCE = [/*lower precedence*/ 'user' /*higher precedence*/]
export const compareSource = (a, b) => -(SOURCE_PRECEDENCE.indexOf(b.source) - SOURCE_PRECEDENCE.indexOf(a.source))
export const comparePrecedence = (a, b) => // TODO: store statically
	Math.max(...a.types.map(t => ENTITY_PRECEDENCE[t] ?? -Infinity)) - Math.max(...b.types.map(t => ENTITY_PRECEDENCE[t] ?? -Infinity))
export const compareLength = (r, overlapped) => (r.end - r.start) - (overlapped.end - overlapped.start)
const compareLayer = (r, overlapped) => (overlapped.layer || 0) - (r.layer || 0)  // "lower" layer takes precedence
const compareWeight = (r, overlapped) => r.weight - overlapped.weight

export const compare = (...comparators) =>
	(a, b) => {
		let r = 0
		for (const c of comparators) {
			if (r = c(a, b)) return r
		}
		return r
	}

const compareWeightThenLength = (r, overlapped) => {
	const c = compareWeight(r, overlapped)
	return c === 0 ? compareLength(r, overlapped) : c
}

const compareLayerThenLength = (r, overlapped) => {
	const c = compareLayer(r, overlapped)
	return c === 0 ? compareLength(r, overlapped) : c
}

export function sort<T extends Range>(ranges: T[]) {
	ranges.sort((a, b) => a.start > b.start ? 1 : a.start < b.start ? -1 : 0)
	return ranges
}

// consider more sophisticated structure/algorithm such as interval tree if too expensive
export class ArrayIntervals<T extends Range> extends Intervals<T> {
	constructor(private ranges: T[] = [], private sorted = false) {
		super()
		if (ranges.length == 0) this.sorted = true
	}
	size() {
		return this.ranges.length
	}
	insert(...ranges: T[]): boolean {
		this.sorted = false
		this.ranges.push(...ranges)
		return true
	}
	append(ranges: Intervals<T>) {
		this.ranges.push(...ranges.getIntervals())
		return this
	}
	filter(fn: (t: T) => boolean) {
		this.ranges = this.ranges.filter(fn)
		return this
	}
	getIntervals(): T[] {
		return this.ranges
	}
	detectOverlap(range: Range, sameSource: boolean = false): T {
		return this._detectOverlap(this.ranges, range, this.sorted, sameSource)
	}
	detectExact(range: Range): T {
		for (const r of this.ranges) {
			if (range.start < r.start && this.sorted) break
			if (r.start == range.start && r.end == range.end) return r
		}
	}
	private intersects(r1: Range, r2: Range) {
		return ((r1.start >= r2.start && r1.start <= r2.end) ||
			(r1.end >= r2.start && r1.end <= r2.end) ||
			// or r2 is wholly within r1
			(r2.start >= r1.start && r2.start <= r1.end))
	}

	private _detectOverlap(ranges: Iterable<T>, range: Range, sorted = false, sameSource = false): T {
		// could be binary search when sorted
		for (const r of ranges) {
			if (sameSource && r === range) continue
			if (range.end < r.start && sorted) break
			if (this.intersects(r, range)) return r
		}
	}

	private _findOverlaps(ranges: Iterable<T>, range: T, sorted = false, sameSource = false): T[] {
		const overlapping = []
		for (const r of ranges) {
			if (sameSource && r === range) continue
			if (range.end < r.start && sorted) break
			if (this.intersects(r, range)) {
				overlapping.push(r)
			}
		}
		return overlapping
	}

	findOverlaps(range, sameSource = false) {
		return this._findOverlaps(this.ranges, range, this.sorted, sameSource)
	}

	/**
	 * from smallest to largest, discard overlapping annotations
	 */
	removeOverlapping(compare = compareLayerThenLength) {
		const nonOverlapping = new Set(this.ranges)
		for (const range of this.ranges) {
			const overlapped = this._detectOverlap(nonOverlapping, range, this.sorted, true)
			if (overlapped) {
				// only need to delete, any remaining overlaps will subsequently be found again and tested
				nonOverlapping.delete(compare(range, overlapped) < 0 ? range : overlapped)
			}
		}
		return new ArrayIntervals(Array.from(nonOverlapping.values()))
	}

	sort() {
		if (this.sorted) return false
		this.ranges.sort((a, b) => a.start > b.start ? 1 : a.start < b.start ? -1 : 0)
		return this.sorted = true
	}

	isSorted() {
		return this.sorted
	}
}

export default class Annotations extends ArrayIntervals<Annotation> {}